/* import __COLOCATED_TEMPLATE__ from './combined-audience-channel-stamp.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type AiContentSegmentsService from 'embercom/services/ai-content-segments-service';
import {
  VOICE_UNSUPPORTED_CATEGORIES,
  CHAT_UNSUPPORTED_CATEGORIES,
} from 'embercom/components/ai-agent/guidance/channel-group-selector';

interface Signature {
  Args: {
    targetChannels: string[];
    aiContentSegmentIds: number[];
    category: string;
  };
  Element: HTMLSpanElement;
}

export default class CombinedAudienceChannelStamp extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare aiContentSegmentsService: AiContentSegmentsService;

  get combinedAudienceChannelLabel(): string {
    let audienceLabel = this.audienceLabel;
    let channelsLabel = this.channelsLabel;
    return this.intl.t('ai-agent.guidance.channel-groups.audience-channel-combined', {
      audience: audienceLabel,
      channels: channelsLabel,
    });
  }

  private get audienceLabel(): string {
    let segmentIds = this.args.aiContentSegmentIds ?? [];
    if (segmentIds.length === 0) {
      return this.intl.t('ai-agent.guidance.channel-groups.everyone');
    }
    if (segmentIds.length === 1) {
      let segment = this.aiContentSegmentsService.segments.find(
        (s) => Number(s.id) === segmentIds[0],
      );
      return segment?.nameWithFallback ?? this.intl.t('ai-agent.guidance.channel-groups.everyone');
    }
    return this.intl.t('ai-agent.guidance.channel-groups.audiences-count', {
      count: segmentIds.length,
    });
  }

  private get channelsLabel(): string {
    let category = this.args.category as any;
    let isChatDisabled = CHAT_UNSUPPORTED_CATEGORIES.includes(category);
    let isVoiceDisabled = VOICE_UNSUPPORTED_CATEGORIES.includes(category);

    let targetChannels = this.args.targetChannels ?? [];

    // Determine the base channel list: explicit selection or expand "all channels"
    let base = targetChannels.length > 0 ? targetChannels : ['chat', 'email', 'voice'];

    // Filter out channels that are disabled for this category
    let effective = base.filter((ch: string) => {
      if (ch === 'voice' && isVoiceDisabled) {
        return false;
      }
      if (ch === 'chat' && isChatDisabled) {
        return false;
      }
      return true;
    });

    if (effective.length === 0 || effective.length === 3) {
      return this.intl.t('ai-agent.guidance.channel-groups.all-channels');
    }
    return effective
      .map((ch: string) => {
        if (ch === 'chat') {
          return this.intl.t('ai-agent.guidance.channel-groups.chat');
        }
        if (ch === 'email') {
          return this.intl.t('ai-agent.guidance.channel-groups.email');
        }
        if (ch === 'voice') {
          return this.intl.t('ai-agent.guidance.channel-groups.voice');
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Guidance::CombinedAudienceChannelStamp': typeof CombinedAudienceChannelStamp;
    'ai-agent/guidance/combined-audience-channel-stamp': typeof CombinedAudienceChannelStamp;
  }
}
